import React, { useState } from "react";
import { applyDiscountCodes, removeDiscountCodes, validateDiscountCode } from "@src/utils/cartCodeUtils";

interface MinicartDiscountCodeProps {
  settings?: {
    // Content settings
    show_discount_section?: boolean;
    discount_label?: string;
    discount_label_text_style?: string;
    discount_placeholder?: string;
    discount_button_text?: string;
    discount_button_text_style?: string;
    discount_toggle_text?: string;
    discount_toggle_text_style?: string;
    discount_remove_text?: string;
    discount_remove_text_style?: string;
    
    // Appearance settings
    color_schema?: string;
    border_radius?: number;
    border?: string;
    border_color?: string;
    
    // Layout & Spacing settings
    direction?: string;
    padding_top?: number;
    padding_bottom?: number;
    padding_left_right?: number;
    margin_bottom?: number;
    
    // Legacy settings (for backward compatibility)
    background_color?: string;
    padding?: number;
  };
  appliedDiscounts?: string[] | Array<{ code: string; applicable?: boolean; }>; // Support both formats
  onDiscountUpdate?: () => void;
}

export const MinicartDiscountCode = ({ 
  settings = {}, 
  appliedDiscounts = [],
  onDiscountUpdate 
}: MinicartDiscountCodeProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const {
    show_discount_section = true,
    discount_label = "Discount Code",
    discount_placeholder = "Enter discount code",
    discount_button_text = "Apply",
    discount_toggle_text = "Have a discount code?",
    discount_remove_text = "Remove",
    background_color = "#f8fafc",
    border_color = "#e2e8f0",
    border_radius = "8px",
    padding = 16,
    margin_bottom = 16,
  } = settings;

  if (!show_discount_section) return null;

  // Normalize appliedDiscounts to string array for consistency
  const normalizedDiscounts = appliedDiscounts?.map(discount => 
    typeof discount === 'string' ? discount : discount.code
  ) || [];

  const containerStyle = {
    backgroundColor: background_color,
    border: `1px solid ${border_color}`,
    borderRadius: border_radius,
    padding: `${Math.max(padding - 4, 8)}px`, // Reduce padding
    marginBottom: `${Math.max(margin_bottom - 4, 8)}px`, // Reduce margin
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    
    setIsLoading(true);
    setError("");

    try {
      // Validate discount code first
      await validateDiscountCode(discountCode);

      // Trigger haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }

      // Apply discount code using utility
      const updatedCodes = [...normalizedDiscounts, discountCode.trim()];
      await applyDiscountCodes(updatedCodes);

      // Success feedback
      if ('vibrate' in navigator) {
        navigator.vibrate([100, 50, 100]);
      }

      setDiscountCode("");
      setIsExpanded(false);
      
      // Notify parent to refresh cart
      if (onDiscountUpdate) {
        onDiscountUpdate();
      }

      // Dispatch cart update event
      window.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { action: 'discount_applied' }
      }));

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply discount code');
      
      // Error haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate([50, 50, 50]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveDiscount = async (codeToRemove: string) => {
    setIsLoading(true);
    setError("");

    try {
      // Trigger haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate(30);
      }

      // Remove discount code using utility
      await removeDiscountCodes([codeToRemove]);

      // Notify parent to refresh cart
      if (onDiscountUpdate) {
        onDiscountUpdate();
      }

      // Dispatch cart update event
      window.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { action: 'discount_removed' }
      }));

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove discount code');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="minicart-discount-code animate-slideInUp" style={containerStyle}>
      {/* Applied Discount Codes */}
      {normalizedDiscounts.length > 0 && (
        <div className="applied-discounts mb-2">
          {normalizedDiscounts.map((code, index) => (
            <div 
              key={index}
              className="flex items-center justify-between border rounded p-2 mb-1 animate-bounceIn bg-green-50 border-green-200"
            >
              <div className="flex items-center">
                <span className="text-green-700 font-medium text-xs">{code}</span>
                <span className="ml-1 text-green-600 text-xs">✓</span>
              </div>
              <button
                onClick={() => handleRemoveDiscount(code)}
                disabled={isLoading}
                className="text-green-600 hover:text-green-800 text-xs font-medium transition-colors duration-200 disabled:opacity-50 px-1"
              >
                {discount_remove_text}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Toggle Button */}
      {!isExpanded && (
        <button
          onClick={() => {
            setIsExpanded(true);
            if ('vibrate' in navigator) {
              navigator.vibrate(30);
            }
          }}
          className="w-full text-left text-blue-600 hover:text-blue-800 font-medium text-xs transition-colors duration-200 btn-animate py-1"
        >
          {discount_toggle_text}
        </button>
      )}

      {/* Discount Input Form */}
      {isExpanded && (
        <div className="discount-form animate-slideInUp">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {discount_label}
          </label>
          
          <div className="flex gap-1">
            <input
              type="text"
              value={discountCode}
              onChange={(e) => {
                setDiscountCode(e.target.value);
                setError("");
              }}
              placeholder={discount_placeholder}
              className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              disabled={isLoading}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleApplyDiscount();
                }
              }}
            />
            <button
              onClick={handleApplyDiscount}
              disabled={isLoading || !discountCode.trim()}
              className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium text-xs transition-all duration-200 btn-animate"
            >
              {isLoading ? (
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                discount_button_text
              )}
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-1 text-red-600 text-xs animate-slideInUp">
              {error}
            </div>
          )}

          {/* Collapse Button */}
          <button
            onClick={() => {
              setIsExpanded(false);
              setDiscountCode("");
              setError("");
              if ('vibrate' in navigator) {
                navigator.vibrate(30);
              }
            }}
            className="mt-2 text-gray-500 hover:text-gray-700 text-xs transition-colors duration-200"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};
